'use client';

import {
  GetTokenResponseDTO,
  PatientLoginRequestDTO,
  PatientOrgMappingDTO,
  PatientPortalLoginOptionsDTO,
  SendOTPSuccessResponseDTO,
} from '@/dtos';
import {
  handleTokenResponse,
  setPatientOrgMapping,
} from '@/utils/auth';
import {
  setAuthHeaders,
} from '@/utils/httpClient';
import {
  notEmptyString,
} from '@/utils/misc';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

type PageState = 'login' | 'otp' | 'orgSelection';

type LoginContextType = {
	loginData: PatientLoginRequestDTO | null;
	setLoginData: (loginData: PatientLoginRequestDTO) => void;
	organizations: PatientOrgMappingDTO[] | null;
	setOrganizations: (org: PatientOrgMappingDTO[] | null) => void;
	loginRequestIdHash: string | null;
	setLoginRequestIdHash: (hash: string | null) => void;
	tokenResponse: GetTokenResponseDTO | null;
	setTokenResponse: (tokenResponse: GetTokenResponseDTO | null) => void;
	selectedOrg: PatientOrgMappingDTO | null;
	setSelectedOrg: (org: PatientOrgMappingDTO | null) => void;
	pageState: PageState;
	setPageState: (state: PageState) => void;
	handleOrgSelection: (org: PatientOrgMappingDTO, token: GetTokenResponseDTO) => Promise<void>;
	isOrgSelectionLoading: boolean;
	otpResponse: SendOTPSuccessResponseDTO | null;
	setOtpResponse: (response: SendOTPSuccessResponseDTO | null) => void;
	authLoginOptions: PatientPortalLoginOptionsDTO | null;
	setAuthLoginOptions: (options: PatientPortalLoginOptionsDTO | null) => void;
}

const LoginContext = createContext<LoginContextType | undefined>(undefined);

type LoginProviderProps = {
	children: ReactNode;
}

export function LoginProvider({ children }: LoginProviderProps) {
  const [loginData, setLoginData] = useState<PatientLoginRequestDTO | null>(null);
  const [organizations, setOrganizations] = useState<PatientOrgMappingDTO[] | null>(null);
  const [loginRequestIdHash, setLoginRequestIdHash] = useState<string | null>(null);
  const [tokenResponse, setTokenResponse] = useState<GetTokenResponseDTO | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<PatientOrgMappingDTO | null>(null);
  const [pageState, setPageState] = useState<PageState>('login');
  const [isOrgSelectionLoading, setIsOrgSelectionLoading] = useState(false);
  const [otpResponse, setOtpResponse] = useState<SendOTPSuccessResponseDTO | null>(null);
  const [authLoginOptions, setAuthLoginOptions] = useState<PatientPortalLoginOptionsDTO | null>(null);

  const handleOrgSelection = useCallback(async (org: PatientOrgMappingDTO, response: GetTokenResponseDTO) => {
    setIsOrgSelectionLoading(true);
    try {
		  setAuthHeaders(response.token ?? '', org.organizationHash ?? '', org.patientHash ?? '');
		  // Store token, refreshToken, and sessionHash in local storage
		  handleTokenResponse(response);
		  setPatientOrgMapping(org);

		  const urlParams = new URLSearchParams(window.location.search);
		  const redirectPath = urlParams.get('redirect');
		  const destination = notEmptyString(redirectPath ?? '') ? decodeURIComponent(redirectPath ?? '') : '/dashboard';
		  window.location.href = destination;

    } catch (error) {
		  console.error('Failed to select organization:', error);
		  // setIsOrgSelectionLoading(false);
    } finally {
      setIsOrgSelectionLoading(false);
    }
	  }, []);

  const value = useMemo(() => ({
    loginData,
    setLoginData,
    organizations,
    setOrganizations,
    loginRequestIdHash,
    setLoginRequestIdHash,
    tokenResponse,
    setTokenResponse,
    selectedOrg,
    setSelectedOrg,
    pageState,
    setPageState,
    handleOrgSelection,
    isOrgSelectionLoading,
    otpResponse,
    setOtpResponse,
    authLoginOptions,
    setAuthLoginOptions,
  }), [
    loginData,
    organizations,
    loginRequestIdHash,
    tokenResponse,
    selectedOrg,
    pageState,
    otpResponse,
    authLoginOptions,
    handleOrgSelection,
    isOrgSelectionLoading,
    setAuthLoginOptions,
  ]);

  return (
    <LoginContext.Provider value={value}>
      {children}
    </LoginContext.Provider>
  );
}

export default function useLoginProvider() {
  const context = useContext(LoginContext);
  if (context === undefined) {
    throw new Error('useLoginProvider must be used within a LoginProvider');
  }
  return context;
}
